import { createResource, createSignal } from "solid-js";
import * as ticketsApi from "../api/tickets";
import type { Status, Ticket } from "../api/tickets";
import { INBOX_SLUG, composeTags } from "../lib/tags";

const [tickets, setTickets] = createSignal<Ticket[]>([]);

const [resource, { refetch }] = createResource(async () => {
  const t = await ticketsApi.listTickets();
  setTickets(t);
  return t;
});

export { tickets, resource, refetch };

// Optimistic update: apply the local change instantly, run the remote request,
// roll back if it fails. All mutations should go through this so the UI stays
// snappy and rollback is consistent.
async function optimistic<T>(
  apply: (prev: Ticket[]) => Ticket[],
  remote: () => Promise<T>,
): Promise<T> {
  const before = tickets();
  setTickets(apply(before));
  try {
    return await remote();
  } catch (err) {
    setTickets(before);
    throw err;
  }
}

export async function updateStatus(id: number, status: Status): Promise<void> {
  await optimistic(
    (ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)),
    () => ticketsApi.updateTicket(id, { status }),
  );
}

export async function updateTitle(id: number, title: string): Promise<void> {
  await optimistic(
    (ts) => ts.map((t) => (t.id === id ? { ...t, title } : t)),
    () => ticketsApi.updateTicket(id, { title }),
  );
}

export async function updateBody(id: number, body: string): Promise<void> {
  await optimistic(
    (ts) => ts.map((t) => (t.id === id ? { ...t, body } : t)),
    () => ticketsApi.updateTicket(id, { body }),
  );
}

export async function createTicket(input: ticketsApi.NewTicket): Promise<Ticket> {
  const created = await ticketsApi.createTicket(input);
  setTickets((ts) => [created, ...ts]);
  return created;
}

export async function deleteTicket(id: number): Promise<void> {
  await optimistic(
    (ts) => ts.filter((t) => t.id !== id),
    () => ticketsApi.deleteTicket(id),
  );
}

// Move one ticket to a different project. Same tag-rewrite class as
// deleteProject, so same treatment: non-optimistic (await + refetch) to avoid
// hand-maintaining the derived project/actor/labels fields after a raw tags
// swap. Restamps actor:human (the human is acting, convention §2) and keeps
// labels. Empty project falls back to inbox (未分類).
export async function updateProject(
  id: number,
  project: string,
): Promise<void> {
  const t = tickets().find((x) => x.id === id);
  if (!t) return;
  const tags = composeTags({
    project: project.trim() || INBOX_SLUG,
    actor: "human",
    labels: t.labels,
  });
  try {
    await ticketsApi.updateTicket(id, { tags });
  } finally {
    await refetch();
  }
}

// Dissolve a project: reassign every ticket under it to the inbox bucket. The
// project then vanishes from the derived Sidebar list. Not optimistic — a
// batch rollback across N tickets is more complexity than this low-frequency
// action warrants, so we update sequentially then refetch the truth. Each
// update restamps actor:human, since the human dissolving the project is the
// one acting (multi-agent convention §2), and preserves the ticket's labels.
export async function deleteProject(name: string): Promise<void> {
  if (name === INBOX_SLUG) return; // never dissolve the fallback bucket
  const affected = tickets().filter((t) => t.project === name);
  try {
    for (const t of affected) {
      const tags = composeTags({
        project: INBOX_SLUG,
        actor: "human",
        labels: t.labels,
      });
      await ticketsApi.updateTicket(t.id, { tags });
    }
  } finally {
    // refetch in finally so a mid-loop failure (e.g. token expiry leaving
    // some tickets reassigned, some not) still syncs the store to the real
    // backend state instead of leaving it stale. The error still propagates
    // to the caller, which surfaces it.
    await refetch();
  }
}
